// TikTok Pixel – NUR nach ausdrücklicher Marketing-Einwilligung laden/initialisieren.
// Pixel-ID ausschließlich aus zentraler Env (REACT_APP_TIKTOK_PIXEL_ID). Kein Secret im Frontend.
// Kein Advanced Matching, keine personenbezogenen Daten. Deduplizierung via geteilter event_id
// (Browser-Event <-> serverseitiges Events-API-Event).
import { hasMarketingConsent } from "@/lib/marketingConsent";

const PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || "";
let initialized = false;
let disabled = false;

export function isConfigured() {
  return !!PIXEL_ID;
}
function ready() {
  return !!PIXEL_ID && initialized && !disabled && hasMarketingConsent();
}

// Standard-TikTok-Loader OHNE automatisches ttq.page() – PageViews steuern wir selbst (SPA).
function bootstrap() {
  const w = window, d = document, t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq = (w[t] = w[t] || []);
  if (ttq._loaded) return ttq;
  ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
  ttq.setAndDefer = function (a, m) { a[m] = function () { a.push([m].concat(Array.prototype.slice.call(arguments, 0))); }; };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.load = function (e, n) {
    const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
    ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
    ttq._o = ttq._o || {}; ttq._o[e] = n || {};
    const s = d.createElement("script"); s.type = "text/javascript"; s.async = true;
    s.src = r + "?sdkid=" + e + "&lib=" + t;
    const f = d.getElementsByTagName("script")[0]; f.parentNode.insertBefore(s, f);
  };
  ttq._loaded = true;
  return ttq;
}

// Initialisiert den Pixel – idempotent, greift NUR bei Einwilligung + konfigurierter Pixel-ID.
// Nach (Erst-)Init bzw. nach Re-Grant wird ein evtl. vorgemerktes CompleteRegistration
// genau einmal nachgefeuert (DSGVO-konform: erst NACH erteilter Einwilligung).
export function initTikTok() {
  disabled = false;
  if (!PIXEL_ID || !hasMarketingConsent()) return;
  if (!initialized) {
    const ttq = bootstrap();
    ttq.load(PIXEL_ID);
    if (typeof ttq.grantConsent === "function") ttq.grantConsent();
    initialized = true;
  } else {
    // Re-Grant nach vorherigem Widerruf: Einwilligung im Pixel wieder aktivieren.
    try { if (window.ttq && typeof window.ttq.grantConsent === "function") window.ttq.grantConsent(); } catch { /* ignore */ }
  }
  flushPendingCompleteRegistration();
}

// Stoppt jegliche weitere Übertragung bei Widerruf.
export function disableTikTok() {
  disabled = true;
  try { if (window.ttq && typeof window.ttq.revokeConsent === "function") window.ttq.revokeConsent(); } catch { /* ignore */ }
}

export function trackPageView() {
  if (!ready()) return;
  window.ttq.page();
}

export function trackEvent(event, params, eventId) {
  if (!ready()) return;
  window.ttq.track(event, params || {}, eventId ? { event_id: eventId } : undefined);
}

function newEventId() {
  try { return crypto.randomUUID(); } catch { return "evt_" + Date.now() + "_" + Math.random().toString(16).slice(2); }
}

// Pending-Replay: Wird CompleteRegistration ohne erteilte Einwilligung ausgelöst, merken wir die
// Conversion (nur die event_id, keine personenbezogenen Daten) einmalig in sessionStorage vor und
// feuern sie nach erteilter Einwilligung genau einmal nach. sessionStorage endet mit dem Tab ->
// keine dauerhafte Speicherung, kein Consent-Bypass.
const PENDING_CREG_KEY = "voyalyo_ttq_pending_creg";
function getPendingCompleteRegistration() {
  try { return sessionStorage.getItem(PENDING_CREG_KEY); } catch { return null; }
}
function setPendingCompleteRegistration(eventId) {
  try { sessionStorage.setItem(PENDING_CREG_KEY, eventId); } catch { /* ignore */ }
}
function clearPendingCompleteRegistration() {
  try { sessionStorage.removeItem(PENDING_CREG_KEY); } catch { /* ignore */ }
}
function flushPendingCompleteRegistration() {
  if (!ready()) return;
  const eventId = getPendingCompleteRegistration();
  if (!eventId) return;
  clearPendingCompleteRegistration();
  window.ttq.track("CompleteRegistration", { content_name: "registration" }, { event_id: eventId });
}

// Funnel-Events (auf TikTok-Standardevents abgebildet, wo möglich).
// BeginRegistration: kein exaktes Standardevent -> Custom-Event "BeginRegistration".
export function trackBeginRegistration() {
  trackEvent("BeginRegistration", { content_name: "registration" });
}
// CompleteRegistration: TikTok-Standardevent. Sendet sofort, wenn Consent + Pixel bereit sind;
// andernfalls wird die Conversion vorgemerkt und nach erteilter Einwilligung nachgefeuert
// (siehe flushPendingCompleteRegistration). Liefert die event_id für serverseitige Deduplizierung.
export function trackCompleteRegistration() {
  const eventId = newEventId();
  if (ready()) {
    trackEvent("CompleteRegistration", { content_name: "registration" }, eventId);
  } else {
    setPendingCompleteRegistration(eventId);
  }
  return eventId;
}
